import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui/Input";
import { ResolveTokenDetails } from "@/components/contracts/ResolveTokenDetails";
import { DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS, MAX_RESOLVE_TOKEN_EXPIRY_HOURS, MIN_RESOLVE_TOKEN_EXPIRY_HOURS, resolveTokenExpiryHoursSchema } from "@/utils/resolveTokenExpiry";
function ReissueSignupLinkModal({ contract, isOpen, isLoading, onClose, onConfirm }) {
    const [resolveTokenExpiryHours, setResolveTokenExpiryHours] = useState(String(DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS));
    const [validationError, setValidationError] = useState();
    const [result, setResult] = useState(null);
    if (!isOpen || !contract) return null;
    function handleClose() {
        setResolveTokenExpiryHours(String(DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS));
        setValidationError(undefined);
        setResult(null);
        onClose();
    }
    async function handleConfirm(event) {
        event.preventDefault();
        const parsedDuration = resolveTokenExpiryHoursSchema.safeParse(Number(resolveTokenExpiryHours));
        if (!parsedDuration.success) {
            setValidationError(parsedDuration.error.issues[0]?.message);
            return;
        }
        try {
            setResult(await onConfirm(parsedDuration.data));
        } catch  {
        // The parent displays the request error.
        }
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "relative top-1/2 mx-auto w-full max-w-2xl -translate-y-1/2 transform rounded-md border bg-white p-5 shadow-lg",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "mb-4 flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            className: "text-lg font-medium text-gray-900",
                            children: result ? 'Signup Link Reissued' : 'Reissue Customer Signup Link'
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: handleClose,
                            className: "text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50",
                            disabled: isLoading,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "sr-only",
                                    children: "Close"
                                }),
                                /*#__PURE__*/ _jsx(X, {
                                    className: "h-6 w-6"
                                })
                            ]
                        })
                    ]
                }),
                result ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(ResolveTokenDetails, {
                            resolveToken: result.resolveToken
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-6 flex justify-end",
                            children: /*#__PURE__*/ _jsx(Button, {
                                variant: "simple",
                                color: "navy-90",
                                onClick: handleClose,
                                children: "Close"
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleConfirm,
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "mb-4 text-sm text-gray-600",
                            children: "Generate a fresh signup link using the newest active contract for this customer."
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mb-4 rounded-md bg-gray-50 p-3 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "font-medium text-gray-900",
                                    children: contract.Name || contract.ContractId
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-1 text-gray-600",
                                    children: [
                                        "Expires: ",
                                        new Date(contract.ExpiryDate).toLocaleDateString()
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Resolve Token Expiry (hours)",
                            type: "number",
                            min: MIN_RESOLVE_TOKEN_EXPIRY_HOURS,
                            max: MAX_RESOLVE_TOKEN_EXPIRY_HOURS,
                            step: 1,
                            required: true,
                            value: resolveTokenExpiryHours,
                            onChange: (event)=>{
                                setResolveTokenExpiryHours(event.currentTarget.value);
                                setValidationError(undefined);
                            },
                            disabled: isLoading,
                            errors: validationError ? {
                                message: validationError
                            } : undefined
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "text-xs text-gray-500",
                            children: [
                                "Token expires between ",
                                MIN_RESOLVE_TOKEN_EXPIRY_HOURS,
                                "-",
                                MAX_RESOLVE_TOKEN_EXPIRY_HOURS,
                                " hours (1 hour to 7 days)"
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-6 flex justify-end space-x-3 border-t pt-4",
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "simple",
                                    buttonStyle: "flat",
                                    onClick: handleClose,
                                    disabled: isLoading,
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "submit",
                                    variant: "simple",
                                    color: "navy-90",
                                    disabled: isLoading,
                                    children: isLoading ? 'Reissuing...' : 'Confirm Reissue'
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
export { ReissueSignupLinkModal };
